import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { Rechnung } from '@/types'
import { Card, CardHeader, CardBody } from '@/components/Card'
import EmptyState from '@/components/EmptyState'
import Spinner from '@/components/Spinner'
import ErrorAlert from '@/components/ErrorAlert'
import { FileText, Download, X, Search, ChevronDown, ChevronRight, Eye, EyeOff, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'

/** Robust date parser for API formats like "06/10/2025 00:00:00" or ISO */
function parseDate(raw: string | null | undefined): Date | null {
  if (!raw) return null
  const m = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  if (m) return new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]))
  const d = new Date(raw)
  return isNaN(d.getTime()) ? null : d
}

function formatDate(raw: string | null | undefined): string {
  const d = parseDate(raw)
  if (!d) return '–'
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function formatEuro(val: number | null | undefined): string {
  if (val == null) return '–'
  return val.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
}

type SortField = 'RechNr' | 'RechEmpfänger' | 'Teilnehmer' | 'RechDatum' | 'GebührEUROBrutto'

function sortValue(r: Rechnung, field: SortField): number | string | null {
  switch (field) {
    case 'RechNr': return r.RechNr ?? null
    case 'RechEmpfänger': return (r.RechEmpfänger ?? '').toLowerCase()
    case 'Teilnehmer': return [r.TeilnehmerNachname, r.TeilnehmerVorname].filter(Boolean).join(' ').toLowerCase()
    case 'RechDatum': return parseDate(r.RechDatum)?.getTime() ?? null
    case 'GebührEUROBrutto': return r.GebührEUROBrutto ?? null
  }
}

interface Props {
  firmenId: number | null
}

export default function FirmaRechnungen({ firmenId }: Props) {
  const [pdfRechnungsId, setPdfRechnungsId] = useState<number | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['rechnungen', firmenId],
    queryFn: () => api.rechnungen(firmenId!),
    enabled: !!firmenId,
  })

  const rechnungen = data?.rechnungen ?? []

  // Sichtbare Rechnungen: ohne parsbares Datum oder mit Platzhalter-RechNr (null, 0,
  // nur aus Neunen) ausblenden.
  const sichtbar = useMemo(() => {
    return rechnungen.filter(r => {
      if (!parseDate(r.RechDatum)) return false
      if (r.RechNr == null || r.RechNr === 0) return false
      if (/^9+$/.test(String(r.RechNr))) return false
      return true
    })
  }, [rechnungen])

  const [search, setSearch] = useState('')
  const [sortField, setSortField] = useState<SortField>('RechDatum')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortField(field); setSortDir(field === 'RechDatum' || field === 'GebührEUROBrutto' ? 'desc' : 'asc') }
  }

  const gefiltert = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return sichtbar
    return sichtbar.filter(r => {
      const haystack = [
        r.RechNr,
        r.RechEmpfänger,
        r.TeilnehmerVorname,
        r.TeilnehmerNachname,
      ].filter(Boolean).join(' ').toLowerCase()
      return haystack.includes(q)
    })
  }, [sichtbar, search])

  const gruppiert = useMemo(() => {
    const map = new Map<number, Rechnung[]>()
    for (const r of gefiltert) {
      const y = parseDate(r.RechDatum)?.getFullYear() ?? 0
      if (!map.has(y)) map.set(y, [])
      map.get(y)!.push(r)
    }
    const sign = sortDir === 'asc' ? 1 : -1
    const cmp = (a: Rechnung, b: Rechnung): number => {
      const va = sortValue(a, sortField)
      const vb = sortValue(b, sortField)
      if (va == null && vb == null) return 0
      if (va == null) return 1
      if (vb == null) return -1
      if (typeof va === 'number' && typeof vb === 'number') return sign * (va - vb)
      return sign * String(va).localeCompare(String(vb), 'de')
    }
    for (const list of map.values()) list.sort(cmp)
    return [...map.entries()].sort(([a], [b]) => b - a)
  }, [gefiltert, sortField, sortDir])

  if (!firmenId) {
    return (
      <Card>
        <CardHeader>Rechnungen</CardHeader>
        <CardBody>
          <EmptyState message="Keine FirmenID verfügbar – Rechnungen können nicht geladen werden" icon />
        </CardBody>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>Rechnungen</CardHeader>
        <CardBody>
          <div className="flex justify-center py-12"><Spinner /></div>
        </CardBody>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>Rechnungen</CardHeader>
        <CardBody>
          <ErrorAlert message={(error as Error).message} onRetry={() => refetch()} />
        </CardBody>
      </Card>
    )
  }

  if (sichtbar.length === 0) {
    return (
      <Card>
        <CardHeader>Rechnungen (0)</CardHeader>
        <CardBody>
          <EmptyState message="Keine Rechnungen für diese Firma" icon />
        </CardBody>
      </Card>
    )
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Suchleiste — prominent */}
      <div className="mb-6 shrink-0">
        <div className="relative">
          <Search size={22} className="absolute left-5 top-1/2 -translate-y-1/2 text-fom-500 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche nach Rechnungsnummer, Empfänger oder Teilnehmer…"
            className="w-full pl-14 pr-12 py-4 text-base rounded-2xl border-2 border-gray-200 bg-white shadow-sm
                       placeholder:text-gray-400 placeholder:font-normal
                       focus:border-fom-500 focus:ring-4 focus:ring-fom-100 focus:shadow-md
                       outline-none transition-all"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 bg-transparent border-none cursor-pointer transition-colors"
              title="Suche zurücksetzen"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Jahres-Akkordeons */}
      <div className="flex-1 min-h-0 overflow-y-auto pr-1">
        {gruppiert.map(([year, rs]) => (
          <YearAccordion
            key={year}
            year={year}
            count={rs.length}
            defaultOpen={false}
          >
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <SortHeader label="Rech-Nr" field="RechNr" sortField={sortField} sortDir={sortDir} onSort={toggleSort} align="left" />
                  <SortHeader label="Empfänger" field="RechEmpfänger" sortField={sortField} sortDir={sortDir} onSort={toggleSort} align="left" />
                  <SortHeader label="Teilnehmer" field="Teilnehmer" sortField={sortField} sortDir={sortDir} onSort={toggleSort} align="left" />
                  <SortHeader label="Datum" field="RechDatum" sortField={sortField} sortDir={sortDir} onSort={toggleSort} align="left" />
                  <SortHeader label="Brutto" field="GebührEUROBrutto" sortField={sortField} sortDir={sortDir} onSort={toggleSort} align="right" />
                  <th className="px-4 py-2.5 border-b border-gray-200 bg-gray-50 w-10" />
                </tr>
              </thead>
              <tbody>
                {rs.map(r => (
                  <RechnungRow
                    key={r.RechnungsID}
                    rechnung={r}
                    onOpenPdf={() => setPdfRechnungsId(r.RechnungsID)}
                  />
                ))}
              </tbody>
            </table>
          </YearAccordion>
        ))}
        {gefiltert.length === 0 && search && (
          <div className="px-6 py-10 text-center text-sm text-gray-400">
            Keine Rechnungen gefunden für „{search}"
          </div>
        )}
      </div>

      {/* PDF Viewer Modal */}
      {pdfRechnungsId && (
        <PdfModal rechnungsId={pdfRechnungsId} onClose={() => setPdfRechnungsId(null)} />
      )}
    </div>
  )
}

function SortHeader({ label, field, sortField, sortDir, onSort, align }: {
  label: string
  field: SortField
  sortField: SortField
  sortDir: 'asc' | 'desc'
  onSort: (f: SortField) => void
  align: 'left' | 'right'
}) {
  const active = sortField === field
  const Icon = !active ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown
  return (
    <th
      onClick={() => onSort(field)}
      className={`px-4 py-2.5 text-[0.66rem] border-b border-gray-200 uppercase tracking-widest font-semibold bg-gray-50 whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 transition-colors ${align === 'right' ? 'text-right' : 'text-left'} ${active ? 'text-fom-700' : 'text-gray-500'}`}
    >
      <span className={`inline-flex items-center gap-1.5 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        <Icon size={12} className={active ? 'text-fom-600' : 'text-gray-400'} />
      </span>
    </th>
  )
}

/* ── Jahres-Akkordeon ──────────────────────────────────── */

function YearAccordion({
  year,
  count,
  defaultOpen,
  children,
}: {
  year: number
  count: number
  defaultOpen: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  const label = year > 0 ? String(year) : 'Ohne Datum'
  return (
    <Card className="mb-3">
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3 bg-transparent border-none cursor-pointer hover:bg-gray-50 text-left"
      >
        {open
          ? <ChevronDown size={16} className="text-gray-500 shrink-0" />
          : <ChevronRight size={16} className="text-gray-500 shrink-0" />}
        <span className="font-semibold text-gray-900 tabular-nums">{label}</span>
        <span className="text-gray-400 text-sm">·</span>
        <span className="text-sm text-gray-500">{count} {count === 1 ? 'Rechnung' : 'Rechnungen'}</span>
      </button>
      {open && (
        <div className="border-t border-gray-100 overflow-x-auto">
          {children}
        </div>
      )}
    </Card>
  )
}

/* ── Einzelne Zeile ────────────────────────────────────── */

function RechnungRow({
  rechnung: r,
  onOpenPdf,
}: {
  rechnung: Rechnung
  onOpenPdf: () => void
}) {
  const isStorno = r.Storno === 'Y'
  const teilnehmerName = [r.TeilnehmerNachname, r.TeilnehmerVorname].filter(Boolean).join(', ') || '–'
  const debitor = r.DebitorenNr != null
    ? (r.DebitorenVorziffer != null ? `${r.DebitorenVorziffer}-${r.DebitorenNr}` : String(r.DebitorenNr))
    : null
  const clickable = r.HatPdf

  return (
    <tr
      onClick={clickable ? onOpenPdf : undefined}
      className={`transition-colors ${clickable ? 'cursor-pointer hover:bg-fom-50/60' : 'cursor-default'} ${isStorno ? 'opacity-50' : ''}`}
      title={clickable ? 'Klicken für PDF-Vorschau' : 'Kein PDF verfügbar'}
    >
      <td className="px-4 py-2.5 border-b border-gray-100 text-[0.8rem] tabular-nums font-medium whitespace-nowrap">
        {r.RechNr ?? r.RechnungsID}
      </td>
      <td className="px-4 py-2.5 border-b border-gray-100 text-[0.8rem] max-w-[240px]">
        <div className="truncate" title={r.RechEmpfänger ?? ''}>{r.RechEmpfänger || '–'}</div>
        {debitor && <div className="text-[0.7rem] text-gray-400 tabular-nums mt-0.5">Debitor {debitor}</div>}
      </td>
      <td className="px-4 py-2.5 border-b border-gray-100 text-[0.8rem]">
        <div>{teilnehmerName}</div>
        {r.Matrikelnr && <div className="text-[0.7rem] text-gray-400 tabular-nums mt-0.5">Matrikel {r.Matrikelnr}</div>}
      </td>
      <td className="px-4 py-2.5 border-b border-gray-100 text-[0.8rem] tabular-nums whitespace-nowrap">
        {formatDate(r.RechDatum)}
      </td>
      <td className="px-4 py-2.5 border-b border-gray-100 text-[0.8rem] tabular-nums font-medium whitespace-nowrap text-right">
        {formatEuro(r.GebührEUROBrutto)}
      </td>
      <td className="px-4 py-2.5 border-b border-gray-100 text-[0.8rem] whitespace-nowrap text-center w-10">
        {clickable ? (
          <Eye size={16} className="inline text-fom-500 group-hover:text-fom-700" aria-label="PDF anzeigen" />
        ) : (
          <EyeOff size={16} className="inline text-gray-300" aria-label="Kein PDF verfügbar" />
        )}
      </td>
    </tr>
  )
}

/* ── PDF Viewer Modal ──────────────────────────────────── */

function PdfModal({ rechnungsId, onClose }: { rechnungsId: number; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-[90vw] max-w-4xl h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50">
          <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <FileText size={16} className="text-fom-500" />
            Rechnung {rechnungsId}
          </span>
          <div className="flex items-center gap-2">
            <a
              href={api.rechnungPdfUrl(rechnungsId)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[0.75rem] font-medium bg-fom-500 text-white hover:bg-fom-600 transition-colors no-underline"
            >
              <Download size={13} />
              Download
            </a>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors border-none cursor-pointer bg-transparent"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        {/* PDF iframe */}
        <div className="flex-1 bg-gray-100">
          <iframe
            src={api.rechnungPdfUrl(rechnungsId)}
            className="w-full h-full border-none"
            title={`Rechnung ${rechnungsId}`}
          />
        </div>
      </div>
    </div>
  )
}
